import {Component} from 'react';
import PropTypes from 'prop-types';
import {bindActionCreators} from 'redux';
import {connect} from 'react-redux';
import {Translate} from 'react-redux-i18n';
import {withRouter} from 'react-router-dom';

import {Icon, TextCheckbox} from 'front-onceforall-core';
import {isEmpty} from 'front-onceforall-core/dist/utils/arrays';

import {addError, addSuccess} from '../../actions/snackbars';
import interventionService from '../../services/intervention/InterventionService';
import projectService from '../../services/project/ProjectService';
import {MANAGER_ROLE, MANAGE_PROJECT_PERMISSION} from '../../utils/constant';
import authenticationService from '../../services/authentication/AuthenticationService';

import ContactSidePanel from './ContactSidePanel';

/**
 *  if organisation is project owner
 *      then use MANAGE_PROJECT_PERMISSION
 *      if i have MANAGE_PROJECT_PERMISSION
 *          then i can check and unchek users include my self if there is another PROJECT MANAGER
 *      else
 *          then i can check and unchek just my self
 *  else if organisation is not project owner
 *      then use MANAGER_ROLE
 *      if i have MANAGER_ROLE
 *          then i can check and uncheck users include myself if there is another Manager
 *      else
 *          then i can check and unchek just my self
 */

export class GrantProjectAccessForm extends Component {
  constructor(props) {
    super(props);

    this.state = {
      projectUserPermissions: [],
      loading: false,
      submitInProgress: false,
      isContactPanelOpen: false
    };

    this.handleViewProjectChanged = this.handleViewProjectChanged.bind(this);
  }

  componentDidMount() {
    this.fetchProjectUserPermissions();
  }

  fetchProjectUserPermissions() {
    this.setState({loading: true});

    projectService.getProjectUserPermissions(this.props.project.projectId, this.props.organisation.organisationId)
      .then(projectUserPermissions => {
        this.setState({projectUserPermissions, loading: false});
      })
      .catch(() => this.setState({loading: false}));
  }

  handleUpdatePermissions = updatedPermissions => (
    interventionService.updateProjectUserPermissions(this.props.interventionId, updatedPermissions)
      .then(() => {
        const myPermission = this.state.projectUserPermissions.find(({user}) => user?.userId === this.props.user.id);

        if (!myPermission?.viewProject && !authenticationService.roles().isBackOffice()) {
          this.props.history.push('/projects');
        }
      })
  );

  handleUpdateEmpty = () => (
    interventionService.updateProjectUserPermissionsAlreadyDone(
      this.props.interventionId,
      {
        projectId: this.props.project.projectId,
        organisationId: this.props.organisation.organisationId
      }
    )
  );

  handleSubmit = event => {
    event.preventDefault();
    if (this.state.submitInProgress) {
      return;
    }

    this.setState({submitInProgress: true, loading: true});

    const updatedPermissions = this.state.projectUserPermissions
      .flatMap(projectUserPermission => (projectUserPermission.updated ?
        ({
          userId: projectUserPermission.user.userId,
          projectId: projectUserPermission.projectId,
          organisationId: projectUserPermission.organisationId,
          viewProject: projectUserPermission.viewProject
        }) : []));

    const promise = isEmpty(updatedPermissions) ?
      this.handleUpdateEmpty : this.handleUpdatePermissions;

    promise(updatedPermissions)
      .then(() => {
        this.props.actions.addSuccess(<Translate value="message.changesSaved"/>);

        if (this.props.isProjectCreationFunnel) {
          this.props.goToNextStep();
        } else {
          this.props.onClose();
        }
      })
      .catch(() => {
        this.props.actions.addError(<Translate value="error.generic"/>);
      })
      .finally(() => {
        this.setState({submitInProgress: false, loading: false});
      });
  };

  canChangeUser(changedUserId) {
    const changedPermission = this.state.projectUserPermissions.find(projectUserPermission => projectUserPermission.user.userId === changedUserId);

    // Can only change if is self or has right (or BackOffice)
    const hasRight = authenticationService.roles().isBackOffice() || this.hasManageProjectPermission(this.state.projectUserPermissions.find(projectUserPermission => projectUserPermission.user.userId === this.props.user.id));
    if (!hasRight && this.props.user.id !== changedUserId) {
      return false;
    }

    // Handle case when project was created by a BO user and no user has view on it, only allow to change user with the permission
    const selectedUsers = this.state.projectUserPermissions.filter(projectUserPermission => projectUserPermission.viewProject);
    if (authenticationService.roles().isBackOffice() && selectedUsers.length === 0) {
      return this.hasManageProjectPermission(changedPermission);
    }

    // Can only change if there still is at least one valid user
    const last = this.isLastProjectUser(changedUserId);
    return !last;
  }

  handleViewProjectChanged(changedUserId) {
    if (!this.canChangeUser(changedUserId)) {
      return;
    }

    const projectUserPermissions = Object.assign([], this.state.projectUserPermissions);
    const changedPermission = projectUserPermissions.find(projectUserPermission => projectUserPermission.user.userId === changedUserId);
    changedPermission.viewProject = !changedPermission.viewProject;
    changedPermission.updated = true;
    this.setState({
      projectUserPermissions
    });
  }

  handleContactPanelCallback = isContactPanelOpen => () => {
    this.setState({isContactPanelOpen});
  };

  isLastProjectUser(userId) {
    return this.state.projectUserPermissions
      .filter(projectUserPermission => this.hasManageProjectPermission(projectUserPermission) && projectUserPermission.viewProject)
      .filter(projectUserPermission => projectUserPermission.user.userId !== userId)
      .length === 0;
  }

  hasManageProjectPermission(projectUserPermission) {
    const isOwnerOrganisation = this.props.project.ownerOrganisation.organisationId === this.props.organisation.organisationId;
    return isOwnerOrganisation ? projectUserPermission.user.organisationPermissions.includes(MANAGE_PROJECT_PERMISSION) : projectUserPermission.user.organisationRole === MANAGER_ROLE;
  }

  render() {
    const rows = this.state.projectUserPermissions.map((projectUserPermission, index) => (
      <tr
        key={index}
        className={this.canChangeUser(projectUserPermission.user.userId) ? '' : 'disabled'}
      >
        <td>
          <span data-id="user-fullName">{projectUserPermission.user.fullName}</span>
        </td>
        <td><Translate value={`user.roles.${projectUserPermission.user.organisationRole}`}/></td>
        <td>
          <TextCheckbox
            key={index}
            checked={projectUserPermission.viewProject}
            onChange={() => this.handleViewProjectChanged(projectUserPermission.user.userId)}
            disabled={!this.canChangeUser(projectUserPermission.user.userId)}
          />
        </td>
      </tr>
    ));

    return (
      <>
        <ContactSidePanel
          isOpen={this.state.isContactPanelOpen}
          onClose={this.handleContactPanelCallback(false)}
        />
        <form
          className="col-12"
          onSubmit={this.handleSubmit}
        >
          {!this.props.isProjectCreationFunnel && (
            <div className="font-size-xl mb-4">
              <strong><Translate value="project.grantAccess.title"/></strong>
            </div>
          )}
          <div style={{position: 'relative'}}>
            {this.state.loading && <div className="loading-overlay"/>}
            <table>
              <thead>
                <tr>
                  <td><Translate value="project.grantAccess.user.fullName"/></td>
                  <td><Translate value="project.grantAccess.user.role"/></td>
                  <td><Translate value="project.grantAccess.user.viewProject"/></td>
                </tr>
              </thead>
              <tbody>
                {rows}
              </tbody>
            </table>
            <div className='mt-5'>
              <span>
                <Translate value="project.grantAccess.missing"/>&nbsp;
                <span
                  className="clickable"
                  onClick={this.handleContactPanelCallback(true)}
                >
                  <Translate value="project.grantAccess.missingLink"/>
                </span>
              </span>
            </div>
          </div>
          <div
            className="d-flex justify-content-end mt-5"
            style={{marginBottom: '70px'}}
          >
            {this.props.isProjectCreationFunnel &&
            <button
              className="large flex-container mr-5"
              type="button"
              disabled={this.state.submitInProgress}
              onClick={this.props.skipStep}
            >
              <Translate value="action.skip"/>
            </button>
            }
            <button
              data-id="submit-button"
              className="large primary inline-container"
              type="submit"
              disabled={this.state.submitInProgress}
            >
              <Icon icon={this.state.submitInProgress ? 'loader' : 'check'}/>
              <Translate value={'action.confirm'}/>
            </button>
          </div>
        </form>
      </>
    );
  }
}

GrantProjectAccessForm.defaultProps = {
  goToNextStep: () => {},
  skipStep: () => {},
  isProjectCreationFunnel: false
};

GrantProjectAccessForm.propTypes = {
  interventionId: PropTypes.number.isRequired,
  user: PropTypes.object.isRequired,
  organisation: PropTypes.object.isRequired,
  project: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  actions: PropTypes.shape({
    addError: PropTypes.func.isRequired,
    addSuccess: PropTypes.func.isRequired
  }).isRequired,
  history: PropTypes.object.isRequired,
  goToNextStep: PropTypes.func,
  skipStep: PropTypes.func,
  isProjectCreationFunnel: PropTypes.bool
};

function mapStateToProps(state) {
  return {
    organisation: state.organisation,
    user: state.user
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({addError, addSuccess}, dispatch)
  };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(GrantProjectAccessForm));
